import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { StorylineChartEvent, StorylineChartSymbol } from '@/mocks/marketStorylinesMock';
import { StorylineEventDetail } from '@/components/StorylineEventDetail';
import { StorylineSectorChart, buildStorylineEventKey } from '@/components/StorylineSectorChart';
import {
  storylineChartEventsService,
  type Glp1StorylineChartEvent,
} from '@/services/api';
import { getStorylineEventColor } from '@/utils/storylineEventColors';

type Props = {
  charts: StorylineChartSymbol[];
  fallbackEvents: StorylineChartEvent[];
  storylineId?: string;
  title?: string;
  className?: string;
};

function toChartEvent(row: Glp1StorylineChartEvent): StorylineChartEvent {
  return {
    date: row.date,
    label: row.label,
    summary: row.summary,
    sentiment: row.sentiment,
    percentChange: row.percentChange,
    primaryCatalyst: row.primaryCatalyst,
    glp1Context: row.glp1Context,
    citations: row.citations,
  };
}

function mergeEventsForSymbol(
  apiEvents: Glp1StorylineChartEvent[] | undefined,
  fallbackEvents: StorylineChartEvent[],
): StorylineChartEvent[] {
  if (apiEvents?.length) {
    return apiEvents.map(toChartEvent);
  }
  return fallbackEvents;
}

export function StorylineStockChartsGrid({
  charts,
  fallbackEvents,
  storylineId = 'glp1-disruption',
  title = 'Stock charts',
  className = '',
}: Props) {
  const [loading, setLoading] = useState(storylineId === 'glp1-disruption');
  const [eventsBySymbol, setEventsBySymbol] = useState<Record<string, StorylineChartEvent[]>>({});
  const [selectedEventKey, setSelectedEventKey] = useState<string | null>(null);
  const chartRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (storylineId !== 'glp1-disruption') {
      const fallback: Record<string, StorylineChartEvent[]> = {};
      for (const chart of charts) {
        fallback[chart.symbol] = fallbackEvents;
      }
      setEventsBySymbol(fallback);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const payload = await storylineChartEventsService.getGlp1DisruptionEvents();
        if (cancelled) return;
        const mapped: Record<string, StorylineChartEvent[]> = {};
        for (const chart of charts) {
          const apiRows = payload?.eventsBySymbol?.[chart.symbol];
          mapped[chart.symbol] = mergeEventsForSymbol(apiRows, fallbackEvents);
        }
        setEventsBySymbol(mapped);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [charts, fallbackEvents, storylineId]);

  const selectEvent = useCallback((symbol: string, event: StorylineChartEvent) => {
    const key = buildStorylineEventKey(symbol, event);
    setSelectedEventKey((current) => (current === key ? null : key));
  }, []);

  const selectedBySymbol = useMemo(() => {
    if (!selectedEventKey) return null;
    const [symbol, date, ...labelParts] = selectedEventKey.split('::');
    const label = labelParts.join('::');
    if (!symbol || !date || !label) return null;
    const event =
      eventsBySymbol[symbol]?.find((row) => row.date === date && row.label === label) ?? null;
    return event ? { symbol, event } : null;
  }, [eventsBySymbol, selectedEventKey]);

  return (
    <div className={className}>
      <div className="mb-6 flex items-baseline justify-between gap-3">
        <p className="text-base font-medium text-gray-900">{title}</p>
        <p className="text-xs text-gray-400">Click a dot or catalyst chip for Exa research</p>
      </div>

      {loading ? (
        <div className="mb-8 flex items-center justify-center rounded-2xl border border-gray-200 bg-white py-16">
          <Loader2 className="mr-2 h-5 w-5 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500">Loading GLP-1 catalyst research…</span>
        </div>
      ) : null}

      <div className="flex flex-col gap-10">
        {charts.map((chart) => {
          const chartEvents = eventsBySymbol[chart.symbol] ?? fallbackEvents;
          const chartSelection =
            selectedBySymbol?.symbol === chart.symbol ? selectedBySymbol : null;

          return (
            <section
              key={chart.symbol}
              id={`glp1-chart-${chart.symbol}`}
              ref={(node) => {
                chartRefs.current[chart.symbol] = node;
              }}
              className="scroll-mt-24 rounded-2xl"
            >
              <StorylineSectorChart
                chartSymbol={chart.symbol}
                chartLabel={chart.label}
                priceSymbol={chart.priceSymbol}
                events={chartEvents}
                days={1825}
                large
                showEventChrome={false}
                eventKeyPrefix={chart.symbol}
                distinctEventColors={storylineId === 'glp1-disruption'}
                selectedEventKey={selectedEventKey}
                onSelectedEventKeyChange={setSelectedEventKey}
              />

              {chartEvents.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2 px-1">
                  {chartEvents.map((event) => {
                    const eventKey = buildStorylineEventKey(chart.symbol, event);
                    const isSelected = selectedEventKey === eventKey;
                    return (
                      <button
                        key={eventKey}
                        type="button"
                        onClick={() => selectEvent(chart.symbol, event)}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs transition-colors ${
                          isSelected
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: getStorylineEventColor(event, { distinct: true }) }}
                        />
                        <span className="max-w-[220px] truncate">{event.label}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {chartSelection ? (
                <StorylineEventDetail
                  symbol={chart.symbol}
                  event={chartSelection.event}
                  onClose={() => setSelectedEventKey(null)}
                  className="mt-4"
                />
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
